import { isFiledStatus, readBondActions } from "@/lib/bondStateModel";
import type { BondBidResultOutcome } from "@/lib/brokerBonds";

// BRK-S141 — the bid result's rules, as this portal needs them to EXPLAIN the
// one the server enforces.
//
// ── ⚠️ A MIRROR, NOT AN AUTHORITY ───────────────────────────────────────────
//
// Discovery holds the order's submitted bid and enforces the threshold. PMS
// deliberately does not pre-check it either and passes discovery's 422 through,
// for a reason worth repeating here: a rule enforced in two places is a rule
// that will disagree with itself, and it would disagree in the direction that
// matters — a client refusing a save the service would have accepted, with the
// broker unable to tell which side was wrong.
//
// So everything here exists to let the panel say WHY the explanation box turned
// required before the round-trip, and to keep it from demanding one the server
// would not. When the two disagree, the SERVER wins: the 422 is rendered inline
// and the local hint is never a gate the broker cannot get past.
//
// PARITY, NOT REUSE — the rule this file mirrors is
// `pms-local lib/bond-bid-result.ts`, itself a mirror of surety's
// `src/bid-result.ts` (SUR-S152). The three repos share no code, so the
// agreement is pinned by `test/lib/bidResult.test.ts` against the same worked
// figures rather than by a dependency.

/**
 * Where OUR BID is read from, in order of preference. Mirrors upstream's
 * `OUR_BID_KEYS`.
 *
 * ⚠️ THIS IS THE OPPOSITE PREFERENCE FROM THE PENAL SUM'S, and the two answer
 * different questions. The penal sum is what the BOND is for, so it prefers
 * `bondAmount`. This wants what the contractor bid FOR THE WORK — the contract
 * price — and the penal sum is only a fallback, because on a bid bond it is a
 * percentage of the price rather than the price itself.
 *
 * The consequence decides the 10% rule: an order carrying only a penal sum has
 * its gap computed against a figure that is not really its bid. That is the
 * honest best available, and the reference Construction configuration makes
 * `estimatedContractPrice` REQUIRED so an order filed through it always carries
 * the right one.
 */
export const OUR_BID_KEYS: readonly string[] = [
  "estimatedContractPrice",
  "bondAmount",
];

/**
 * Our bid as the order carries it, or **null** when it carries no usable
 * amount.
 *
 * ONLY A FINITE NUMBER COUNTS. A formatted string is refused rather than parsed
 * — D-10's display/wire split runs both ways, and a currency value is a number
 * on the wire, so a string here is a client that wrote around the validator.
 *
 * NULL rather than 0: a defaulted zero would make every loss look like a bid
 * infinitely below the field, which is the one answer that demands an
 * explanation nobody can give.
 */
export function ourBidFromOrder(
  data: Readonly<Record<string, unknown>> | null | undefined,
): number | null {
  if (!data) return null;
  for (const key of OUR_BID_KEYS) {
    const value = data[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

/**
 * Greg's threshold, as a PERCENTAGE.
 *
 * ⚠️ `10`, not `0.10` — the gap it is compared against is a percentage too
 * (`12.5` means 12.5%), which is what the contract's `details.gapPercent` key
 * publishes and what the panel renders. Named rather than inlined because it
 * appears in the copy as well as in the comparison, and two spellings of a
 * threshold is how a sentence stops describing the rule.
 */
export const THRESHOLD_PERCENT = 10;

/**
 * How far under the next lowest bid ours came in, as a PERCENTAGE rounded to
 * two places — for DISPLAY.
 *
 * The comparison below is against the unrounded value, so a 10.004% gap is over
 * the line and reads as `10`. A negative result is a bid ABOVE the next lowest,
 * which is a real thing to render (they lost on something other than price).
 */
export function bidGapPercent(ourBid: number, nextLowestBid: number): number {
  return (
    Math.round(((nextLowestBid - ourBid) / nextLowestBid) * 100 * 100) / 100
  );
}

/**
 * Is the gap past the threshold? STRICTLY greater, so exactly 10% is not — an
 * inclusive comparison would demand an explanation for a bid that is merely
 * competitive.
 *
 * ⚠️ COMPARED WITHOUT DIVIDING, on purpose, and this is the one line in the
 * file that is a bug fix rather than a transcription. `(100000 - 90000) /
 * 100000` is a clean 10% on paper and a binary float can land either side of a
 * comparison written `> 10`; discovery computes it in exact decimal, so a
 * mirror using doubles would demand an explanation the live service does not
 * (or waive one it does) on the width of a rounding error. Cross-multiplying
 * keeps both sides in the integers money is actually made of.
 */
export function isPastThreshold(
  ourBid: number,
  nextLowestBid: number,
): boolean {
  return (nextLowestBid - ourBid) * 100 > nextLowestBid * THRESHOLD_PERCENT;
}

export interface BidExplanationCheck {
  outcome: "won" | "lost";
  /** From the ORDER, never from the form — see `ourBidFromOrder`. */
  ourBidAmount: number | null | undefined;
  nextLowestBidAmount: number | null | undefined;
  explanation: string | null | undefined;
}

/**
 * Is an explanation required, and still missing?
 *
 * Five conditions, and every one of them is a reason NOT to demand one:
 *
 *   • the bid was WON — the rule is about a job we lost and priced oddly, not
 *     about winning cheaply, however far under we came in;
 *   • an explanation has already been given;
 *   • we do not know OUR OWN bid. That is a gap in OUR data rather than in the
 *     broker's answer, so it must not refuse their record — upstream is
 *     explicit about this, and it is the condition a client is most likely to
 *     get wrong, because the natural instinct is to treat a missing figure as
 *     the strictest case;
 *   • we do not know the next lowest — a ratio needs two figures;
 *   • the next lowest is not positive: a zero next-lowest bid is not a bid, and
 *     the ratio computed from one is meaningless.
 */
export function bidExplanationRequired(check: BidExplanationCheck): boolean {
  if (check.outcome !== "lost") return false;
  if (
    check.explanation !== undefined &&
    check.explanation !== null &&
    check.explanation.trim() !== ""
  ) {
    return false;
  }
  const ours = check.ourBidAmount;
  const theirs = check.nextLowestBidAmount;
  if (typeof ours !== "number" || typeof theirs !== "number") return false;
  if (!(theirs > 0)) return false;
  return isPastThreshold(ours, theirs);
}

// ─── The bid result screen's gates (BRK-S183) ────────────────────────────────
//
// Three questions the profile and the screen both ask of an ORDER, answered in
// one place so the entry point, the Update affordance and the close-out
// sentence cannot disagree between surfaces. Each reads only what the order
// view states; none of them is a rule the server does not also enforce, and
// where the two could differ the server's refusal is what renders.

/** The order facts the gates read — a structural slice of `BondSubmission`. */
export interface BidResultOrderFacts {
  status: string;
  flow?: string;
  subproductId: string;
  closeReason?: string | null;
  actions?: unknown;
  bidResult?: { outcome: BondBidResultOutcome } | null;
}

/**
 * Is the Bid result entry point offered on this order at all?
 *
 * A FILED bond order only (`isFiledStatus`, BRK-S399 / BRK-S432), never a
 * withdrawn or declined one: a draft has no filed bid (409 `NOT_SUBMITTED`),
 * a standalone pre-approval has no bid at all (BRK-S160), and a PERFORMANCE &
 * PAYMENT order is refused one upstream (`BID_RESULT_NOT_APPLICABLE`,
 * SUR-S181) — so it is not offered rather than offered and refused.
 *
 * `ppSubproductId` is the catalog's P&P subproduct (`undefined` while the
 * catalog is being read). While it is unknown the entry point is WITHHELD
 * rather than shown and then withdrawn; a catalog with no P&P subproduct
 * (`null`) suppresses nothing.
 */
export function offersBidResult(
  order: BidResultOrderFacts,
  ppSubproductId: string | null | undefined,
): boolean {
  // Filed, in the v2 vocabulary too (BRK-S399, BRK-S432) — which excludes
  // withdrawn, declined and cancelled. A CLOSED order keeps it: that is where
  // a lost result lives, and the server decides whether it is still
  // correctable (decision 9).
  if (!isFiledStatus(order.status)) return false;
  if ((order.flow ?? "bond") !== "bond") return false;
  if (ppSubproductId === undefined) return false;
  return order.subproductId !== ppSubproductId;
}

/**
 * Why a recorded result cannot be updated, in the broker's words — or null
 * when it can.
 *
 * PMS's `actions.bidResult` decides (PMS-S837): on while the bid bond is out
 * and on a lost bid (decision 9: a lost → won correction may reopen it), off
 * with the matrix's sentence once a win is recorded, the order closes or the
 * bid bond is voided by a pending edit. The portal used to read the stage off
 * `workflowStatus`, which left the broker contract (BRK-S511). A block it
 * cannot read locks with a neutral sentence rather than offering a write the
 * server did not grant (NN #7).
 */
export function bidResultLockReason(order: BidResultOrderFacts): string | null {
  const action = readBondActions(order.actions)?.bidResult;
  if (action === undefined) {
    return "The bid result can’t be changed right now.";
  }
  if (action.enabled) return null;
  return action.reason ?? "The bid result can’t be changed right now.";
}

/**
 * Has the SERVER said the lost bid closed the request out?
 *
 * The close-out sentence ("…closed and the capacity it was holding has been
 * released") is rendered only on this — `status: "closed"` with
 * `closeReason: "not_awarded"` beside a lost result — and never from a 2xx
 * alone (NN #7: no false success).
 */
export function lostBidClosedOut(order: BidResultOrderFacts): boolean {
  return (
    order.bidResult?.outcome === "lost" &&
    order.status === "closed" &&
    order.closeReason === "not_awarded"
  );
}

// ─── The recorded outcome, labelled (BRK-S402) ───────────────────────────────

const OUTCOME_LABELS: Record<string, string> = {
  won: "Won",
  lost: "Lost",
  // PMS-S844 (P9) — recorded from the account's aggregate bar.
  low_bidder: "Low bidder · awaiting award",
};

/**
 * A recorded outcome, as the portal names it. An outcome this build has not
 * adopted reads "Recorded" — a result IS on file — never a blank, and never
 * "Lost", which is what a two-way ternary would have called a low bidder.
 */
export function bidOutcomeLabel(outcome: unknown): string {
  return typeof outcome === "string" && Object.hasOwn(OUTCOME_LABELS, outcome)
    ? OUTCOME_LABELS[outcome]
    : "Recorded";
}
